export type PerformanceSpanName =
  | "open_project"
  | "load_rqtpack_manifest"
  | "load_rqtpack_assets_index"
  | "parse_rqt_file"
  | "parse_changed_range"
  | "semantic_check"
  | "resolve_import_graph"
  | "lower_to_ir"
  | "optimize_ir"
  | "assemble_prompt"
  | "retrieve_memory"
  | "render_preview"
  | "compose_character_layers"
  | "save_project_state"
  | "write_cache"
  | "run_test_case"
  | "full_build";

export type PerformanceCacheStatus = "hit" | "miss" | "stale" | "bypass" | "unknown";
export type PerformanceThread = "main" | "worker" | "unknown";
export type PerformanceErrorStatus = "ok" | "error" | "cancelled";

export interface PerformanceSpanMeta {
  inputSize?: number;
  fileId?: string;
  packageId?: string;
  cache?: PerformanceCacheStatus;
  thread?: PerformanceThread;
  invalidationReason?: string;
  errorStatus?: PerformanceErrorStatus;
  detail?: Record<string, string | number | boolean | null | undefined>;
}

export interface PerformanceMeasurement {
  name: PerformanceSpanName;
  durationMs: number;
  inputSize: number;
  fileId: string;
  packageId: string;
  cache: PerformanceCacheStatus;
  thread: PerformanceThread;
  invalidationReason: string;
  errorStatus: PerformanceErrorStatus;
  startedAt: number;
  endedAt: number;
  detail?: Record<string, string | number | boolean | null | undefined>;
}

export interface PerformanceRecorder {
  record(measurement: PerformanceMeasurement): void;
}

let recorder: PerformanceRecorder | null = null;

export function setPerformanceRecorder(next: PerformanceRecorder | null): () => void {
  const previous = recorder;
  recorder = next;
  return () => {
    recorder = previous;
  };
}

export function getPerformanceRecorder(): PerformanceRecorder | null {
  return recorder;
}

export function isPerformanceRecordingEnabled(): boolean {
  return recorder !== null;
}

export class InMemoryPerformanceRecorder implements PerformanceRecorder {
  readonly measurements: PerformanceMeasurement[] = [];

  record(measurement: PerformanceMeasurement): void {
    this.measurements.push(measurement);
  }

  clear(): void {
    this.measurements.length = 0;
  }
}

export function measurePerformance<T>(
  name: PerformanceSpanName,
  meta: PerformanceSpanMeta | (() => PerformanceSpanMeta),
  fn: () => T,
): T {
  const active = recorder;
  if (!active) return fn();

  const startedAt = nowMs();
  try {
    const value = fn();
    active.record(toMeasurement(name, resolveMeta(meta), startedAt, nowMs(), "ok"));
    return value;
  } catch (error) {
    active.record(toMeasurement(name, resolveMeta(meta), startedAt, nowMs(), errorStatusFor(error)));
    throw error;
  }
}

export async function measurePerformanceAsync<T>(
  name: PerformanceSpanName,
  meta: PerformanceSpanMeta | (() => PerformanceSpanMeta),
  fn: () => Promise<T>,
): Promise<T> {
  const active = recorder;
  if (!active) return fn();

  const startedAt = nowMs();
  try {
    const value = await fn();
    active.record(toMeasurement(name, resolveMeta(meta), startedAt, nowMs(), "ok"));
    return value;
  } catch (error) {
    active.record(toMeasurement(name, resolveMeta(meta), startedAt, nowMs(), errorStatusFor(error)));
    throw error;
  }
}

export function recordPerformanceMeasurement(
  name: PerformanceSpanName,
  meta: PerformanceSpanMeta,
  startedAt: number,
  endedAt: number,
): void {
  recorder?.record(toMeasurement(name, meta, startedAt, endedAt, meta.errorStatus ?? "ok"));
}

export function nowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

export function estimateInputSize(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === "string") return value.length;
  if (value instanceof Uint8Array) return value.byteLength;
  if (value instanceof ArrayBuffer) return value.byteLength;
  if (typeof Blob !== "undefined" && value instanceof Blob) return value.size;
  if (Array.isArray(value)) return value.length;
  return 0;
}

export function estimateJsonInputSize(value: unknown): number {
  if (!isPerformanceRecordingEnabled()) return 0;
  try {
    return JSON.stringify(value).length;
  } catch {
    return 0;
  }
}

function toMeasurement(
  name: PerformanceSpanName,
  meta: PerformanceSpanMeta,
  startedAt: number,
  endedAt: number,
  fallbackStatus: PerformanceErrorStatus,
): PerformanceMeasurement {
  return {
    name,
    durationMs: Math.max(0, endedAt - startedAt),
    inputSize: Math.max(0, Math.floor(meta.inputSize ?? 0)),
    fileId: meta.fileId ?? "",
    packageId: meta.packageId ?? "",
    cache: meta.cache ?? "unknown",
    thread: meta.thread ?? "unknown",
    invalidationReason: meta.invalidationReason ?? "",
    errorStatus: meta.errorStatus ?? fallbackStatus,
    startedAt,
    endedAt,
    ...(meta.detail ? { detail: meta.detail } : {}),
  };
}

function resolveMeta(meta: PerformanceSpanMeta | (() => PerformanceSpanMeta)): PerformanceSpanMeta {
  return typeof meta === "function" ? meta() : meta;
}

function errorStatusFor(error: unknown): PerformanceErrorStatus {
  if (error && typeof error === "object" && "name" in error) {
    const name = String((error as { name?: unknown }).name);
    if (name === "AbortError" || name === "CanceledError") return "cancelled";
  }
  return "error";
}
